import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import TenantContext from '@/contexts/TenantContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Database,
  Plus,
  ArrowRight,
  RefreshCw,
  Clock,
  Sparkles,
  Trash2,
  ChevronRight,
  ChevronLeft,
  Activity,
  Code,
  Users,
  Search,
  AlertCircle,
  Zap,
  ShieldCheck,
  Settings,
  Hammer,
  Edit2,
  Package,
  Tag,
  Layers,
  Star,
  Box,
  UploadCloud,
  Settings2,
  Download,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Schema de campos estructurados por tipo de silo (Fase 12)
const SILO_FIELD_SCHEMAS = {
  catalogos: {
    label: 'Catálogo',
    newAction: 'Publicar SKU',
    editAction: 'Modificar SKU',
    fields: [
      { key: 'sku', label: 'SKU', type: 'text', required: false, placeholder: 'Ej. LAPTOP-HP-001' },
      { key: 'nombre', label: 'Nombre del Producto', type: 'text', required: true, placeholder: 'Ej. Consultoría 1hr, Camisa Polo...' },
      { key: 'descripcion', label: 'Descripción', type: 'textarea', required: false, placeholder: 'Descripción detallada del producto o servicio...' },
      { key: 'categoria', label: 'Categoría', type: 'text', required: false, placeholder: 'Ej. Ropa, Servicios Digitales...' },
      { key: 'precio', label: 'Precio', type: 'currency', required: true, placeholder: '0.00' },
      { key: 'cantidad', label: 'Stock / Cantidad', type: 'number', required: false, placeholder: '0' },
      {
        key: 'estado', label: 'Estado', type: 'select', required: false, default: 'ACTIVO',
        options: [
          { value: 'ACTIVO', label: 'Activo' },
          { value: 'INACTIVO', label: 'Inactivo' }
        ]
      }
    ]
  },
  clientes: {
    label: 'Clientes CRM',
    newAction: 'Inyectar Cliente',
    editAction: 'Modificar Cliente',
    fields: [
      { key: 'nombre', label: 'Nombre Completo', type: 'text', required: true, placeholder: 'Ej. Elon Musk' },
      { key: 'rfc', label: 'RFC (Identificación Fiscal)', type: 'text', required: false, placeholder: 'XAXX010101000' },
      { key: 'telefono', label: 'WhatsApp (Teléfono)', type: 'text', required: true, placeholder: '+52 1 55 ...' },
      { key: 'email', label: 'Correo Electrónico', type: 'text', required: false, placeholder: 'contacto@empresa.com' },
      {
        key: 'tipo', label: 'Tipo de Cliente', type: 'select', required: false, default: 'LEAD',
        options: [
          { value: 'LEAD', label: 'Lead (Frio)' },
          { value: 'FRECUENTE', label: 'Frecuente' },
          { value: 'VIP', label: 'Cliente VIP' },
          { value: 'CORPORATIVO', label: 'Corporativo' }
        ]
      },
      { key: 'ltv', label: 'LTV Acumulado ($)', type: 'number', required: false, placeholder: '0.00' },
      { key: 'direccion', label: 'Dirección Completa', type: 'textarea', required: false, placeholder: 'Calle, Número, Colonia...' },
      { key: 'notas', label: 'Notas de Inteligencia', type: 'textarea', required: false, placeholder: 'Contexto para la IA...' }
    ]
  },
  citas: {
    label: 'Agenda de Citas',
    newAction: 'Agendar Cita',
    editAction: 'Modificar Cita',
    fields: [
      { key: 'folio', label: 'Folio / Ref', type: 'text', required: true, placeholder: 'Ej. CIT-1001' },
      { key: 'cliente', label: 'Participante / Cliente', type: 'text', required: true, placeholder: 'Nombre del cliente...' },
      { key: 'fecha_hora', label: 'Fecha y Hora', type: 'datetime-local', required: true, placeholder: '' },
      { key: 'servicio', label: 'Servicio / Motivo', type: 'text', required: false, placeholder: 'Ej. Consultoría, Corte...' },
      { key: 'notas', label: 'Notas Adicionales', type: 'textarea', required: false, placeholder: 'Indicaciones especiales...' },
      {
        key: 'estado', label: 'Estado', type: 'select', required: false, default: 'Programada',
        options: [
          { value: 'Programada', label: 'Programada' },
          { value: 'Completada', label: 'Completada' },
          { value: 'Cancelada', label: 'Cancelada' }
        ]
      }
    ]
  },
  tickets: {
    label: 'Soporte y Tickets',
    newAction: 'Registrar Ticket',
    editAction: 'Actualizar Ticket',
    fields: [
      { key: 'folio', label: 'Folio / ID', type: 'text', required: true, placeholder: 'Ej. TIC-1234' },
      { key: 'cliente', label: 'Cliente / Solicitante', type: 'text', required: true, placeholder: 'Seleccionar cliente CRM...' },
      { key: 'asunto', label: 'Asunto / Título', type: 'text', required: true, placeholder: 'Ej. Falla en servidor...' },
      { key: 'descripcion', label: 'Descripción del Problema', type: 'textarea', required: false, placeholder: 'Detalles técnicos...' },
      {
        key: 'prioridad', label: 'Prioridad', type: 'select', required: true, default: 'Media',
        options: [
          { value: 'Baja', label: 'Baja' },
          { value: 'Media', label: 'Media' },
          { value: 'Alta', label: 'Alta' },
          { value: 'Urgente', label: 'Urgente' }
        ]
      },
      {
        key: 'estado', label: 'Estado Actual', type: 'select', required: true, default: 'Pendiente',
        options: [
          { value: 'Pendiente', label: 'Pendiente' },
          { value: 'Proceso', label: 'En Proceso' },
          { value: 'Resuelto', label: 'Resuelto' }
        ]
      }
    ]
  },
  ventas: {
    label: 'Libro de Ventas',
    newAction: 'Registrar Venta',
    editAction: 'Actualizar Venta',
    fields: [
      { key: 'folio', label: 'Folio', type: 'text', required: true, placeholder: 'VEN-XXXX' },
      { key: 'cliente', label: 'Cliente CRM', type: 'text', required: true, placeholder: 'Seleccionar cliente...' },
      { key: 'producto_servicio', label: 'Producto / Servicio', type: 'text', required: true, placeholder: '¿Qué se vendió?' },
      { key: 'cantidad', label: 'Cantidad', type: 'number', required: true, default: 1 },
      { key: 'precio_unitario', label: 'Precio Unitario', type: 'currency', required: true },
      { key: 'descuento', label: 'Descuento Directo', type: 'currency', required: false, default: 0 },
      { key: 'subtotal', label: 'Subtotal (Neto)', type: 'currency', required: false },
      { key: 'total', label: 'Total (Caja)', type: 'currency', required: true },
      {
        key: 'metodo_pago', label: 'Método de Pago', type: 'select', required: true, default: 'Transferencia',
        options: [
          { value: 'Transferencia', label: 'Transferencia' },
          { value: 'Efectivo', label: 'Efectivo' },
          { value: 'Tarjeta', label: 'Tarjeta' },
          { value: 'Crypto', label: 'Crypto' }
        ]
      },
      {
        key: 'estado_pago', label: 'Estado Pago', type: 'select', required: true, default: 'Completado',
        options: [
          { value: 'Completado', label: 'Liquidado' },
          { value: 'Pendiente', label: 'Pendiente' },
          { value: 'Parcial', label: 'Parcial' }
        ]
      },
      {
        key: 'estado_venta', label: 'Estado Venta', type: 'select', required: true, default: 'Entregado',
        options: [
          { value: 'Entregado', label: 'Entregado' },
          { value: 'Transito', label: 'En Tránsito' },
          { value: 'Cancelado', label: 'Cancelado' }
        ]
      },
      { key: 'vendedor', label: 'Vendedor / Agente', type: 'text', required: false },
      {
        key: 'canal_venta', label: 'Canal', type: 'select', required: false, default: 'Directo',
        options: [
          { value: 'Directo', label: 'Venta Directa' },
          { value: 'WhatsApp', label: 'WhatsApp / Chat' },
          { value: 'Web', label: 'Portal Web' },
          { value: 'Referido', label: 'Referido' }
        ]
      },
      { key: 'notas', label: 'Notas Internas', type: 'textarea', required: false }
    ]
  }
};

const DataEnginePage = ({ collectionParam, hideHeader }) => {
  const navigate = useNavigate();
  const tenantContext = useContext(TenantContext);
  const contextTenantSlug = tenantContext?.tenantSlug;
  const contextTenant = tenantContext?.tenant;

  // Manual selection for Master Dashboard
  const [selectedTenantSlug, setSelectedTenantSlug] = useState('');
  const [tenants, setTenants] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [showTenantSelector, setShowTenantSelector] = useState(false);

  const tenantSlug = contextTenantSlug || selectedTenantSlug;
  const activeTenant = contextTenant || tenants.find(t => t.id.replace('tenant_', '') === selectedTenantSlug) || { name: 'Seleccionar Tenant' };

  const [silos, setSilos] = useState([]);
  const [globalModules, setGlobalModules] = useState([]);
  const [engineTab, setEngineTab] = useState('silos'); // 'silos' | 'matrix'
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSilo, setSelectedSilo] = useState(null);
  const [siloData, setSiloData] = useState([]);
  const [isInjecting, setIsInjecting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);

  // State for Schema-based Sidebar (Fase 12)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkFormat, setBulkFormat] = useState("json");
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldType, setNewFieldType] = useState("text");
  const [schemaFormData, setSchemaFormData] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [siloSearchTerm, setSiloSearchTerm] = useState('');
  const [siloFilter, setSiloFilter] = useState('');
  const [formFields, setFields] = useState([{ key: 'nombre', value: '' }, { key: 'precio', value: '' }]);

  // Handlers de Exportación
  const handleExport = (format) => {
    if (siloData.length === 0) {
      toast.error("No hay registros para exportar");
      return;
    }

    const dataToExport = siloData.map(p => {
      // Intentar obtener esquema del silo para mapear labels
      const siloSchema = SILO_FIELD_SCHEMAS[selectedSilo] || { fields: [] };
      let base = { _id: p._id };

      // Mapear campos del esquema
      siloSchema.fields.forEach(f => {
        base[f.label] = p[f.key] || '';
      });

      // Incluir campos personalizados (los que no están en el esquema base)
      Object.keys(p).forEach(key => {
        if (!siloSchema.fields.find(f => f.key === key) && !['_id', 'fecha_registro'].includes(key)) {
          base[key] = p[key];
        }
      });

      base.Fecha_Registro = p.fecha_registro;
      return base;
    });

    const fileName = `Export_${selectedSilo}_${tenantSlug}_${new Date().toISOString().split('T')[0]}`;

    if (format === 'json') {
      const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.json`;
      a.click();
    } else if (format === 'csv') {
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.csv`;
      a.click();
    } else if (format === 'xlsx') {
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Registros");
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    }

    setIsExportMenuOpen(false);
    toast.success(`Exportación ${format.toUpperCase()} iniciada`);
  };

  // Configuración de Silos
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configuringSilo, setConfiguringSilo] = useState(null);
  const [configData, setConfigData] = useState({ display_name: '', active: true, ai_context: '' });
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const primaryColor = activeTenant?.primary_color || '#06b6d4';
  const glassBg = "bg-white/5 backdrop-blur-xl border-white/10";

  useEffect(() => {
    // Fetch all tenants for the master selector (only if not in tenant portal)
    if (!contextTenantSlug) {
      const fetchAllTenants = async () => {
        try {
          const response = await axios.get(`${API_URL}/api/tenants`);
          setTenants(response.data);
          if (response.data.length > 0 && !selectedTenantSlug) {
            // No auto-select, wait for user
          }
        } catch (error) {
          console.error("Error fetching tenants:", error);
        }
      };
      fetchAllTenants();
    }
  }, [contextTenantSlug]);

  // Auto-calculation for Ventas in Data Engine (Parity Phase 12)
  useEffect(() => {
    if (selectedSilo?.name === 'ventas' && isSidebarOpen) {
      const cant = parseFloat(schemaFormData.cantidad) || 0;
      const price = parseFloat(schemaFormData.precio_unitario) || 0;
      const desc = parseFloat(schemaFormData.descuento) || 0;

      const newSubtotal = cant * price;
      const newTotal = newSubtotal - desc;

      if (schemaFormData.subtotal !== newSubtotal || schemaFormData.total !== newTotal) {
        setSchemaFormData(prev => ({
          ...prev,
          subtotal: newSubtotal,
          total: newTotal
        }));
      }
    }
  }, [schemaFormData.cantidad, schemaFormData.precio_unitario, schemaFormData.descuento, selectedSilo, isSidebarOpen, schemaFormData.subtotal, schemaFormData.total]);

  const fetchClientes = async () => {
    if (!tenantSlug) return;
    try {
      const res = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/clientes`);
      setClientes(res.data || []);
    } catch (err) {
      console.error("Error fetching clients for engine:", err);
    }
  };

  const fetchSilos = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('fluxoco_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos`);
      setSilos(response.data);

      // Fetch global modules from Admin API (Unified Phase 10)
      if (!contextTenantSlug) {
        const modRes = await axios.get(`${API_URL}/api/admin/modules`, { headers });
        setGlobalModules(modRes.data);
      }

    } catch (error) {
      toast.error("Error al conectar con la maquinaria de datos");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const toggleModuleMatrix = async (modId) => {
    try {
      const token = localStorage.getItem('fluxoco_token');
      const isActive = silos.some(s => s.name === modId && s.active !== false);

      await axios.patch(`${API_URL}/api/tenants/${tenantSlug}/collections/${modId}/config`, {
        active: !isActive
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`Interruptor ${!isActive ? 'encendido' : 'apagado'} para ${modId}`);
      fetchSilos();
    } catch (error) {
      toast.error("Fallo de conexión electromecánica en la Matriz");
    }
  };

  const handleActivateKit = async () => {
    if (!tenantSlug) {
      toast.error('Selecciona un cliente primero');
      return;
    }
    if (!window.confirm('¿Activar el Kit Universal 5x5 (Catálogos, Ventas, Tickets, Citas y CRM) para este tenant?')) return;

    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/tenants/${tenantSlug}/collections/activate-kit`);
      toast.success('¡Estructura Base Activada/Actualizada!');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error activating kit:', error);
      toast.error('Error al activar el kit');
    } finally {
      setLoading(false);
    }
  };

  const exploreSilo = async (silo) => {
    setSelectedSilo(silo);
    try {
      const response = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/${silo.name}`);
      setSiloData(response.data);

      // Cargar campos personalizados desde la configuración del silo si existen
      if (silo.custom_fields) {
        setCustomFields(silo.custom_fields);
      } else {
        setCustomFields([]);
      }

      // Auto-detect fields from existing data to make injection easier
      if (response.data.length > 0) {
        const sampleKeys = Object.keys(response.data[0]).filter(k => k !== '_id' && k !== 'engine_injected_at');
        setFields(sampleKeys.map(k => ({ key: k, value: '' })));
      } else {
        setFields([{ key: 'nombre', value: '' }, { key: 'precio', value: '' }]);
      }
    } catch (error) {
      toast.error("Error al explorar el almacén");
    }
  };

  const handleAddField = () => setFields([...formFields, { key: '', value: '' }]);
  const handleRemoveField = (index) => setFields(formFields.filter((_, i) => i !== index));
  const updateField = (index, field, val) => {
    const newFields = [...formFields];
    newFields[index][field] = val;
    setFields(newFields);
  };

  const applyPreset = (preset) => {
    setIsEditing(false);
    setEditingRecordId(null);
    const newFields = Object.entries(preset).map(([k, v]) => ({ key: k, value: v }));
    setFields(newFields);
    setIsSidebarOpen(true);
  };

  // Abre formulario para nuevo registro (schema o genérico)
  const openNewForm = () => {
    setIsEditing(false);
    setEditingRecordId(null);
    const schema = SILO_FIELD_SCHEMAS[selectedSilo?.name];
    if (schema) {
      const defaultData = {};
      schema.fields.forEach(f => { defaultData[f.key] = f.default || ''; });

      // Auto-generate folio for citas and tickets
      if (selectedSilo?.name === 'citas') {
        defaultData.folio = `CIT-${Math.floor(1000 + Math.random() * 9000)}`;
      }
      if (selectedSilo?.name === 'ventas') {
        defaultData.folio = `VEN-${Math.floor(1000 + Math.random() * 9000)}`;
      }
      if (selectedSilo?.name === 'tickets') {
        defaultData.folio = `TIC-${Math.floor(1000 + Math.random() * 9000)}`;
      }

      // Initialize custom fields too
      customFields.forEach(f => { defaultData[f.name] = ''; });
      setSchemaFormData(defaultData);
    } else {
      setFields([{ key: 'nombre', value: '' }, { key: 'precio', value: '' }]);
    }
    setIsSidebarOpen(true);
  };

  const handleEditClick = (record) => {
    const { _id, engine_injected_at, engine_updated_at, ...data } = record;
    setEditingRecordId(_id);
    setIsEditing(true);
    const schema = SILO_FIELD_SCHEMAS[selectedSilo?.name];
    if (schema) {
      setSchemaFormData(data);
    } else {
      const fields = Object.entries(data).map(([key, value]) => ({ key, value: String(value) }));
      setFields(fields.length > 0 ? fields : [{ key: '', value: '' }]);
    }
    setIsSidebarOpen(true);
  };

  // Guardar registro con schema estructurado
  const handleSchemaSave = async () => {
    const schema = SILO_FIELD_SCHEMAS[selectedSilo?.name];
    if (!schema) return;
    const missing = schema.fields.filter(f => f.required && !schemaFormData[f.key]);
    if (missing.length > 0) {
      toast.error(`Campos obligatorios: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    try {
      setIsInjecting(true);
      const payload = { ...schemaFormData };

      // Asegurar que todos los campos del schema y personalizados estén presentes
      const allFields = [...schema.fields, ...customFields];
      allFields.forEach(f => {
        const key = f.key || f.name;
        if ((f.type === 'currency' || f.type === 'number') && payload[key] !== undefined && payload[key] !== '') {
          payload[key] = parseFloat(payload[key]) || 0;
        }
      });

      // Sync status for tickets (compatibility with specialized module)
      if (selectedSilo?.name === 'tickets' && payload.estado) {
        payload.status = payload.estado;
      }

      // Sync for ventas (compatibility)
      if (selectedSilo?.name === 'ventas') {
        payload.monto = payload.total;
        payload.concepto = payload.producto_servicio;
      }

      if (!isEditing) {
        payload.fecha_registro = new Date().toISOString();
        if (selectedSilo.name === 'clientes') {
          payload.fecha_alta = payload.fecha_registro;
        }
      }
      if (isEditing && editingRecordId) {
        await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/${selectedSilo.name}/${editingRecordId}`, payload);
        toast.success('Registro actualizado');
      } else {
        await axios.post(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/${selectedSilo.name}/inject`, { data: payload });
        toast.success('Registro guardado');
      }
      setIsSidebarOpen(false);
      setIsEditing(false);
      setEditingRecordId(null);
      exploreSilo(selectedSilo);
    } catch (error) {
      toast.error(isEditing ? 'Error al actualizar' : 'Error al guardar');
    } finally {
      setIsInjecting(false);
    }
  };

  // Helper: color de estado para badges
  const getStatusColor = (status) => {
    switch ((status || '').toUpperCase()) {
      case 'ACTIVO': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50';
      case 'INACTIVO': return 'bg-rose-500/20 text-rose-400 border-rose-500/50';
      case 'AGOTADO': return 'bg-amber-500/20 text-amber-400 border-amber-500/50';
      default: return 'bg-white/10 text-slate-300 border-white/20';
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar este registro permanentemente?")) return;

    try {
      setRefreshing(true);
      await axios.delete(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/${selectedSilo.name}/${recordId}`);
      toast.success("Registro eliminado del silo");
      exploreSilo(selectedSilo);
    } catch (error) {
      toast.error("Error al eliminar el registro");
    } finally {
      setRefreshing(false);
    }
  };

  const handleOpenConfig = (e, silo) => {
    e.stopPropagation();
    setConfiguringSilo(silo);
    // Inicializar con datos del silo o defaults
    setConfigData({
      display_name: silo.display_name || silo.name,
      active: silo.active !== false,
      ai_context: silo.ai_context || ''
    });
    setDeleteConfirmation(''); // Reset confirmation string
    setShowConfigModal(true);
  };

  const saveSiloConfig = async () => {
    try {
      setLoading(true);
      await axios.patch(`${API_URL}/api/tenants/${tenantSlug}/collections/${configuringSilo.name}/config`, configData);
      toast.success('Configuración guardada correctamente');
      setShowConfigModal(false);
      fetchSilos();
    } catch (error) {
      console.error('Error saving silo config:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const deleteSilo = async () => {
    if (deleteConfirmation !== configuringSilo.name) {
      toast.warning("El nombre de confirmación no coincide");
      return;
    }

    try {
      setIsDeleting(true);
      await axios.delete(`${API_URL}/api/tenants/${tenantSlug}/collections/${configuringSilo.name}`);
      toast.success(`Silo ${configuringSilo.name} eliminado correctamente`);
      setShowConfigModal(false);
      fetchSilos();
    } catch (error) {
      console.error('Error deleting silo:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el silo');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleInject = async () => {
    try {
      const payload = {};
      formFields.forEach(f => {
        if (f.key) {
          const val = f.value;
          payload[f.key] = (!isNaN(val) && val !== '') ? Number(val) : val;
        }
      });

      if (Object.keys(payload).length === 0) {
        toast.error("Campos vacíos");
        return;
      }

      setIsInjecting(true);
      if (isEditing && editingRecordId) {
        await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/${selectedSilo.name}/${editingRecordId}`, payload);
        toast.success("Registro actualizado exitosamente");
      } else {
        await axios.post(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/${selectedSilo.name}/inject`, { data: payload });
        toast.success("Dato guardado correctamente");
      }

      // Reset state
      setIsSidebarOpen(false);
      setIsEditing(false);
      setEditingRecordId(null);
      exploreSilo(selectedSilo);
    } catch (error) {
      toast.error(isEditing ? "Fallo al actualizar" : "Fallo al guardar");
    } finally {
      setIsInjecting(false);
    }
  };

  const handleBulkUpload = async () => {
    const tSlug = tenantSlug || contextTenantSlug || 'master';
    try {
      let records = [];
      if (bulkFormat === 'json') {
        records = JSON.parse(bulkText);
        if (!Array.isArray(records)) records = [records];
      } else {
        const lines = bulkText.split('\n').filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        records = lines.slice(1).map(line => {
          const values = line.split(',');
          return headers.reduce((obj, h, i) => {
            obj[h] = values[i]?.trim();
            return obj;
          }, {});
        });
      }

      await axios.post(`${API_URL}/api/tenants/${tSlug}/engine/silos/${selectedSilo.name}/bulk-inject`, { records });
      toast.success(`${records.length} registros cargados exitosamente`);
      setIsBulkModalOpen(false);
      setBulkText("");
      exploreSilo(selectedSilo);
    } catch (error) {
      console.error("Bulk Upload Error:", error);
      toast.error("Error al procesar los datos masivos. Verifica el formato.");
    }
  };

  const handleAddCustomField = async () => {
    if (!newFieldName) return;
    const tSlug = tenantSlug || contextTenantSlug || 'master';
    const fieldName = newFieldName.toLowerCase().replace(/\s+/g, '_');

    const updatedFields = [...customFields, { name: fieldName, label: newFieldName, type: newFieldType }];

    try {
      await axios.patch(`${API_URL}/api/tenants/${tSlug}/engine/silos/${selectedSilo.name}/schema`, { fields: updatedFields });
      setCustomFields(updatedFields);
      toast.success(`Campo "${newFieldName}" añadido al silo`);
      setIsAddFieldModalOpen(false);
      setNewFieldName("");
    } catch (error) {
      toast.error("Error al añadir campo");
    }
  };

  const renderFormFields = (fields) => {
    const allFields = [...fields, ...customFields];
    return allFields.map((field) => {
      const key = field.key || field.name;

      // Especial para Citas: Dividir Fecha y Hora
      if (selectedSilo?.name === 'citas' && key === 'fecha_hora') {
        let f = '';
        let h = '';
        if (schemaFormData[key]) {
          try {
            const [datePart, timePart] = schemaFormData[key].split('T');
            f = datePart;
            h = timePart?.substring(0, 5) || '';
          } catch (e) { }
        }

        return (
          <div key={key} className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Fecha *</label>
              <input
                type="date"
                className="w-full bg-slate-900 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:border-blue-500/50 outline-none transition-all focus:bg-slate-800"
                value={f}
                onChange={(e) => setSchemaFormData({ ...schemaFormData, [key]: `${e.target.value}T${h || '00:00'}:00` })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Hora *</label>
              <input
                type="time"
                className="w-full bg-slate-900 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:border-blue-500/50 outline-none transition-all focus:bg-slate-800"
                value={h}
                onChange={(e) => setSchemaFormData({ ...schemaFormData, [key]: `${f || new Date().toISOString().split('T')[0]}T${e.target.value}:00` })}
              />
            </div>
          </div>
        );
      }

      return (
        <div key={key} className="space-y-2">
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-1">
            {field.label} {field.required && <span className="text-rose-500">*</span>}
          </label>
          {field.type === 'select' ? (
            <div className="relative">
              <select
                className="w-full bg-slate-900 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:border-blue-500/50 outline-none transition-all focus:bg-slate-800 appearance-none font-bold"
                value={schemaFormData[key] || field.default || ''}
                onChange={(e) => setSchemaFormData({ ...schemaFormData, [key]: e.target.value })}
                required={field.required}
              >
                <option value="" className="bg-slate-900">Seleccionar...</option>
                {field.options?.map(opt => (
                  <option key={opt.value || opt} value={opt.value || opt} className="bg-slate-900">{opt.label || opt}</option>
                ))}
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                <Plus className="w-4 h-4 rotate-45" />
              </div>
            </div>
          ) : field.type === 'textarea' ? (
            <textarea
              className="w-full bg-slate-900 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:border-blue-500/50 outline-none transition-all min-h-[100px] resize-none focus:bg-slate-800 placeholder:text-slate-700"
              placeholder={field.placeholder}
              value={schemaFormData[key] || ''}
              onChange={(e) => setSchemaFormData({ ...schemaFormData, [key]: e.target.value })}
              required={field.required}
            />
          ) : (
            <div className="relative">
              {field.type === 'currency' && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">$</span>}
              <input
                type={['currency', 'number'].includes(field.type) ? 'number' : (['datetime-local', 'date', 'time'].includes(field.type) ? field.type : 'text')}
                list={key === 'cliente' ? 'engine-clients-list' : undefined}
                readOnly={key === 'folio'}
                step={field.type === 'currency' ? '0.01' : '1'}
                className={`w-full bg-slate-900 border border-white/10 rounded-2xl ${field.type === 'currency' ? 'pl-8' : 'px-4'} py-3 text-sm text-white focus:border-blue-500/50 outline-none transition-all focus:bg-slate-800 placeholder:text-slate-700 ${key === 'folio' ? 'opacity-50 cursor-not-allowed font-black tracking-widest' : ''}`}
                placeholder={field.placeholder}
                value={schemaFormData[key] || ''}
                onChange={(e) => setSchemaFormData({ ...schemaFormData, [key]: e.target.value })}
                required={field.required}
              />
              {key === 'cliente' && (
                <datalist id="engine-clients-list">
                  {clientes.map(c => <option key={c._id} value={c.nombre} />)}
                </datalist>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  useEffect(() => {
    if (tenantSlug) {
      fetchClientes();
      if (collectionParam) {
        exploreSilo({ name: collectionParam, display_name: collectionParam.toUpperCase() });
        setLoading(false);
      } else {
        fetchSilos();
      }
    } else {
      setLoading(false);
    }
  }, [tenantSlug, collectionParam]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
      <LoaderComponent color={primaryColor} />
      <p className="text-slate-500 font-black animate-pulse uppercase tracking-[0.3em] text-[10px]">Sincronizando Motores...</p>
    </div>
  );

  if (!tenantSlug) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
      <div className="relative">
        <div className="absolute inset-0 bg-blue-500/20 blur-3xl rounded-full" />
        <div className="relative p-8 rounded-full bg-white/5 border border-white/10 text-blue-400">
          <Users className="w-12 h-12" />
        </div>
      </div>
      <div className="text-center space-y-2">
        <h3 className="text-2xl font-black text-white uppercase tracking-tighter italic">Arquitectura Desconectada</h3>
        <p className="text-slate-500 font-medium max-w-sm">
          Selecciona un cliente de la lista superior para configurar su motor de datos y silos.
        </p>
      </div>
      {!contextTenantSlug && (
        <div className="relative">
          <button
            onClick={() => setShowTenantSelector(!showTenantSelector)}
            className="flex items-center gap-3 px-8 py-4 rounded-2xl bg-white text-black font-black text-xs uppercase tracking-widest hover:bg-blue-500 hover:text-white transition-all shadow-xl active:scale-95 group"
          >
            {showTenantSelector ? 'Cerrar Selector' : 'Configurar Motor de Datos'}
            <ChevronRight className={`w-4 h-4 text-blue-400 transition-transform ${showTenantSelector ? 'rotate-90' : ''}`} />
          </button>

          <AnimatePresence>
            {showTenantSelector && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute left-1/2 -translate-x-1/2 top-full mt-4 w-72 bg-[#0F172A] border border-white/10 rounded-3xl shadow-2xl z-50 overflow-hidden"
              >
                <div className="p-4 border-b border-white/10 bg-white/5">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text"
                      placeholder="Buscar cliente..."
                      autoFocus
                      value={tenantSearch}
                      onChange={(e) => setTenantSearch(e.target.value)}
                      className="w-full bg-slate-900/50 border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-xs text-white focus:outline-none focus:border-blue-500/50"
                    />
                  </div>
                </div>
                <div className="max-h-[250px] overflow-y-auto p-2">
                  {tenants.filter(t => t.name.toLowerCase().includes(tenantSearch.toLowerCase())).map(t => (
                    <button
                      key={t.id}
                      onClick={() => {
                        setSelectedTenantSlug(t.id.replace('tenant_', ''));
                        setShowTenantSelector(false);
                        setLoading(true); // Trigger reload
                      }}
                      className="w-full text-left px-4 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all mb-1 last:mb-0 text-slate-400 hover:bg-blue-600 hover:text-white"
                    >
                      {t.name}
                    </button>
                  ))}
                  {tenants.length === 0 && (
                    <div className="p-4 text-center text-slate-500 text-[10px] font-black uppercase tracking-widest">
                      No hay clientes registrados
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-8 pb-32">
      {/* Header */}
      {!hideHeader && (
        <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-5 h-5 text-blue-500" />
              <span className="text-[10px] uppercase tracking-[0.4em] font-black text-blue-500/80">Sistemas de Información</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit whitespace-nowrap">
              Fluxo Data <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Engine</span>
            </h1>
            <p className="text-slate-500 mt-2 font-medium italic text-sm">Administración simplificada de silos para {activeTenant?.name}</p>
          </motion.div>

          <div className="flex flex-col items-end gap-4 w-full lg:w-auto">
            {/* Top Row: Search + Sync */}
            <div className="flex flex-wrap items-center justify-end gap-4 w-full">
              {selectedSilo && (
                <div className="relative w-full lg:w-[300px]">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-slate-500" />
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-11 pr-4 py-3 bg-slate-900/50 border border-white/10 rounded-2xl text-sm placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                    placeholder={`Buscar en ${selectedSilo.name}...`}
                    value={siloSearchTerm}
                    onChange={(e) => setSiloSearchTerm(e.target.value)}
                  />
                </div>
              )}

              {/* Tenant Selector (Only for Master Admin) */}
              {!contextTenantSlug && (
                <div className="relative">
                  <button
                    onClick={() => setShowTenantSelector(!showTenantSelector)}
                    className="flex items-center gap-3 px-6 h-12 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all text-xs font-black text-white group shadow-xl"
                  >
                    <Users className="w-4 h-4 text-blue-400" />
                    <span>{activeTenant?.name}</span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${showTenantSelector ? 'rotate-90' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {showTenantSelector && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowTenantSelector(false)} />
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute right-0 top-full mt-2 w-64 bg-[#0F172A] border border-white/10 rounded-3xl shadow-2xl z-50 overflow-hidden"
                        >
                          <div className="p-3 border-b border-white/10">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                              <input
                                type="text"
                                placeholder="Buscar cliente..."
                                value={tenantSearch}
                                onChange={(e) => setTenantSearch(e.target.value)}
                                className="w-full bg-white/5 border border-white/5 rounded-xl py-2 pl-9 pr-3 text-xs text-white focus:outline-none focus:border-blue-500/50"
                              />
                            </div>
                          </div>
                          <div className="max-h-[200px] overflow-y-auto p-1.5">
                            {tenants.filter(t => t.name.toLowerCase().includes(tenantSearch.toLowerCase())).map(t => (
                              <button
                                key={t.id}
                                onClick={() => {
                                  setSelectedTenantSlug(t.id.replace('tenant_', ''));
                                  setShowTenantSelector(false);
                                }}
                                className={`w-full text-left px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all mb-1 last:mb-0 ${tenantSlug === t.id.replace('tenant_', '') ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}
                              >
                                {t.name}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              )}

              <button
                onClick={fetchSilos}
                disabled={refreshing || !tenantSlug}
                className="flex items-center justify-center gap-3 px-6 h-12 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all text-xs font-black text-slate-300 group shadow-xl"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                Sincronizar Datos
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Header Tabs & Actions Row */}
      {tenantSlug && !hideHeader && (
        <div className="flex flex-col lg:flex-row justify-between items-center gap-4 mb-8">
          {/* Tabs Menu */}
          <div className="flex gap-4 p-1.5 bg-slate-900/50 rounded-2xl w-fit border border-white/5">
            <button
              onClick={() => setEngineTab('silos')}
              className={`flex items-center gap-2 px-8 py-2.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-300 ${engineTab === 'silos'
                  ? 'bg-blue-600 text-white shadow-xl shadow-blue-500/30 scale-[1.05]'
                  : 'text-slate-500 hover:text-white hover:bg-white/5 border border-transparent'
                }`}
            >
              <Database className={`w-4 h-4 transition-colors ${engineTab === 'silos' ? 'text-white' : 'text-slate-500'}`} />
              Silos Activos
            </button>
            {/* Matrix Tab only for Master Admin */}
            {!contextTenantSlug && (
              <button
                onClick={() => setEngineTab('matrix')}
                className={`flex items-center gap-2 px-8 py-2.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-300 ${engineTab === 'matrix'
                    ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-500/30 scale-[1.05]'
                    : 'text-slate-500 hover:text-white hover:bg-white/5 border border-transparent'
                  }`}
              >
                <Sparkles className={`w-4 h-4 transition-colors ${engineTab === 'matrix' ? 'text-white' : 'text-slate-500'}`} />
                Matriz de Interruptores
              </button>
            )}
          </div>

          {/* Silo Searcher (Only when in silos tab and no silo selected) */}
          {engineTab === 'silos' && !selectedSilo && (
            <div className="relative w-full lg:w-72">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-slate-500" />
              </div>
              <input
                type="text"
                className="block w-full pl-11 pr-4 py-3 bg-slate-900/50 border border-white/10 rounded-2xl text-[10px] placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-black uppercase tracking-widest shadow-xl"
                placeholder="Filtrar Silos..."
                value={siloFilter}
                onChange={(e) => setSiloFilter(e.target.value)}
              />
            </div>
          )}

          {/* Action Buttons (Aligned with Tabs) */}
          {selectedSilo && (
            <div className="flex flex-wrap items-center justify-end gap-3">
              <div className="flex items-center bg-white/5 border border-white/10 rounded-2xl p-1 gap-1">
                <button
                  onClick={() => document.getElementById('silo-table-container').scrollBy({ left: -300, behavior: 'smooth' })}
                  className="p-2.5 hover:bg-white/10 rounded-xl text-slate-400 hover:text-white transition-all"
                  title="Desplazar Izquierda"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="w-px h-4 bg-white/10 mx-1" />
                <button
                  onClick={() => document.getElementById('silo-table-container').scrollBy({ left: 300, behavior: 'smooth' })}
                  className="p-2.5 hover:bg-white/10 rounded-xl text-slate-400 hover:text-white transition-all"
                  title="Desplazar Derecha"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <div className="relative">
                  <button
                    onClick={() => setIsExportMenuOpen(!isExportMenuOpen)}
                    className={`group relative overflow-hidden rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 h-12 transition-all duration-300 flex items-center justify-center gap-2 ${isExportMenuOpen ? 'ring-2 ring-blue-500' : ''}`}
                    title="Exportar Datos"
                  >
                    <Download className="w-5 h-5 text-emerald-400" />
                    <span className="font-bold text-[10px] uppercase tracking-widest">Exportar</span>
                  </button>

                  <AnimatePresence>
                    {isExportMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-[60]" onClick={() => setIsExportMenuOpen(false)} />
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          className="absolute right-0 mt-2 w-48 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl z-[70] overflow-hidden p-2"
                        >
                          <button
                            onClick={() => handleExport('xlsx')}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 rounded-xl transition-colors text-left group/item"
                          >
                            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 group-hover/item:bg-emerald-500/20">
                              <Download className="w-4 h-4 text-emerald-400" />
                            </div>
                            <div>
                              <p className="text-[10px] font-black text-white uppercase tracking-wider">Excel (.xlsx)</p>
                              <p className="text-[8px] text-slate-500 font-bold uppercase">Formato Premium</p>
                            </div>
                          </button>
                          <button
                            onClick={() => handleExport('csv')}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 rounded-xl transition-colors text-left group/item"
                          >
                            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/20 group-hover/item:bg-blue-500/20">
                              <Database className="w-4 h-4 text-blue-400" />
                            </div>
                            <div>
                              <p className="text-[10px] font-black text-white uppercase tracking-wider">CSV (Tabular)</p>
                              <p className="text-[8px] text-slate-500 font-bold uppercase">Universal</p>
                            </div>
                          </button>
                          <div className="h-px bg-white/5 my-1" />
                          <button
                            onClick={() => handleExport('json')}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 rounded-xl transition-colors text-left group/item"
                          >
                            <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center border border-amber-500/20 group-hover/item:bg-amber-500/20">
                              <X className="w-4 h-4 text-amber-400 rotate-45" />
                            </div>
                            <div>
                              <p className="text-[10px] font-black text-white uppercase tracking-wider">JSON (Raw)</p>
                              <p className="text-[8px] text-slate-500 font-bold uppercase">Respaldo</p>
                            </div>
                          </button>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
                <button
                  onClick={() => setIsBulkModalOpen(true)}
                  className="group relative overflow-hidden rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white px-5 h-12 transition-all duration-300 flex items-center justify-center gap-2"
                  title="Carga Masiva"
                >
                  <UploadCloud className="w-5 h-5 text-blue-400" />
                  <span className="font-bold text-[10px] uppercase tracking-widest">Carga Masiva</span>
                </button>
                <button
                  onClick={openNewForm}
                  className="group relative overflow-hidden rounded-2xl bg-blue-600 hover:bg-blue-500 text-white px-6 h-12 shadow-xl shadow-blue-500/20 transition-all duration-300 hover:shadow-blue-500/30 hover:scale-[1.02] flex items-center justify-center gap-2"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-blue-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                  <Plus className="w-5 h-5 relative z-10" />
                  <span className="font-bold text-sm uppercase tracking-widest relative z-10">Nueva Entrada</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* RENDER MATRIX TAB */}
      {engineTab === 'matrix' && !contextTenantSlug && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <div className="p-8 rounded-[3rem] bg-slate-900/40 border border-emerald-500/10 backdrop-blur-sm shadow-2xl">
            <div className="flex items-center gap-4 mb-8 pb-8 border-b border-white/5">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 shadow-[0_0_30px_rgba(16,185,129,0.2)]">
                <Zap className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-white italic font-outfit uppercase tracking-tight">Matriz Granular</h2>
                <p className="text-slate-400 text-sm font-medium">Sobrescribe los permisos dictados por la plantilla vertical originaria de este tenant.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {globalModules.map(mod => {
                const isActive = silos.some(s => s.name === mod.id && s.active !== false);
                return (
                  <div key={mod.id} onClick={() => toggleModuleMatrix(mod.id)} className={`p-4 rounded-2xl cursor-pointer transition-all border flex items-center gap-4 ${isActive ? 'bg-emerald-500/10 border-emerald-500/40 shadow-inner' : 'bg-white/5 border-white/5 hover:border-white/20 hover:bg-white/10'}`}>
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 border transition-all duration-300 ${isActive ? 'bg-emerald-500 border-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.6)]' : 'bg-slate-950/60 border-white/10'}`}>
                      {isActive && <div className="w-2.5 h-2.5 bg-white rounded-sm drop-shadow-md animate-in zoom-in" />}
                    </div>
                    <div className="flex flex-col">
                      <span className={`text-sm font-black uppercase tracking-tight transition-colors ${isActive ? 'text-white' : 'text-slate-300'}`}>{mod.label || mod.name}</span>
                      <span className={`text-[10px] font-mono transition-colors ${isActive ? 'text-emerald-400/80' : 'text-slate-500'}`}>{mod.id}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* Grid of Silos or Data Hub Invitation */}
      {engineTab === 'silos' && !selectedSilo && (
        (silos.length > 0) ? (
          <div className="mt-4">

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {silos.filter(s =>
                (s.name || '').toLowerCase().includes(siloFilter.toLowerCase()) ||
                (s.display_name || '').toLowerCase().includes(siloFilter.toLowerCase())
              ).map((silo, i) => (
                <motion.div
                  key={silo.full_name}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card className={`${glassBg} hover:border-blue-500/30 transition-all duration-500 cursor-pointer group relative overflow-hidden h-full shadow-lg`} onClick={() => exploreSilo(silo)}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <div className="p-4 rounded-2xl bg-blue-500/10 text-blue-400 border border-blue-500/10 shadow-inner">
                          <Database className="w-6 h-6" />
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            exploreSilo(silo);
                            toast.info(`Cargando silo: ${silo.display_name || silo.name}`);
                          }}
                          className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-black tracking-widest uppercase transition-all shadow-lg shadow-blue-900/40 active:scale-95 flex items-center gap-2"
                        >
                          <Plus className="w-3 h-3" />
                          Gestionar Silo
                        </button>
                      </div>
                      <h3 className="text-lg font-black text-white uppercase tracking-tighter">{silo.name}</h3>
                      <div className="flex items-center gap-6 mt-8">
                        <div>
                          <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Entradas</p>
                          <p className="text-3xl font-black text-white mt-1">{silo.count}</p>
                        </div>
                        <div className="h-10 w-px bg-white/5" />
                        <div className="flex-1">
                          <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Estado</p>
                          <p className="text-[10px] font-bold text-slate-400 mt-2 uppercase">Online & Sync</p>
                        </div>
                      </div>
                      <div
                        onClick={(e) => handleOpenConfig(e, silo)}
                        className="mt-8 flex items-center justify-between text-[10px] font-black text-blue-400/60 uppercase tracking-widest group-hover:text-blue-400 transition-colors cursor-pointer hover:bg-white/5 p-2 rounded-xl -mx-2"
                      >
                        <span>Configuración</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Redirect to Studio Only for Admins */}
            {!contextTenantSlug && (
              <div className="flex justify-center mt-12 pb-12">
                <button
                  onClick={() => navigate('/blueprint-studio')}
                  className="group flex items-center gap-3 px-8 py-4 rounded-2xl bg-white/5 border border-dashed border-white/10 hover:border-blue-500/50 hover:bg-blue-500/5 transition-all text-[10px] font-black uppercase text-slate-500 hover:text-blue-400 tracking-widest"
                >
                  <Hammer className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                  Forjar Nuevo Silo (Studio)
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center pt-6">
            {/* Data Hub Invitation Empty State */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full max-w-3xl p-8 rounded-[3rem] bg-gradient-to-br from-blue-600/5 to-purple-600/5 border border-dashed border-white/10 flex flex-col items-center text-center space-y-6"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-500/20 blur-3xl rounded-full animate-pulse" />
                <div className="relative w-20 h-20 rounded-3xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                  <Zap className="w-10 h-10 text-cyan-400" />
                </div>
              </div>

              <div className="space-y-3">
                <h2 className="text-3xl font-black text-white tracking-tight uppercase italic">Arquitectura <span className="text-cyan-400">en Proceso</span></h2>
                <p className="text-slate-500 max-w-lg mx-auto font-medium">
                  Tu motor de inteligencia aún no tiene silos activos. {!contextTenantSlug ? 'Inyecta el Kit Universal 5x5 para comenzar.' : 'Contacta a soporte para habilitar tu primer silo de datos.'}
                </p>
              </div>

              {!contextTenantSlug ? (
                <>
                  <div className="flex flex-wrap justify-center gap-4 w-full">
                    {[
                      { label: '🛒 Catálogos', color: 'text-blue-400' },
                      { label: '💰 Ventas', color: 'text-emerald-400' },
                      { label: '🎫 Tickets', color: 'text-amber-400' },
                      { label: '📅 Citas', color: 'text-purple-400' },
                      { label: '👥 CRM', color: 'text-cyan-400' }
                    ].map((module) => (
                      <div key={module.label} className="min-w-[120px] p-4 rounded-2xl bg-white/5 border border-white/5 flex flex-col items-center gap-2 transition-transform hover:scale-105 hover:bg-white/10">
                        <ShieldCheck className={`w-4 h-4 ${module.color}`} />
                        <span className="text-[9px] font-black text-white uppercase tracking-widest">{module.label}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handleActivateKit}
                    className="group relative px-10 py-4 bg-cyan-500 hover:bg-cyan-400 text-black font-black text-[11px] uppercase tracking-[0.2em] rounded-2xl transition-all shadow-[0_0_30px_rgba(34,211,238,0.3)] active:scale-95"
                  >
                    Activar Maquinaria de Datos
                  </button>

                  {!contextTenantSlug && (
                    <button
                      onClick={() => navigate('/blueprint-studio')}
                      className="text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-cyan-400 transition-colors"
                    >
                      + Ir al Studio para Diseñar Nuevo Silo
                    </button>
                  )}
                </>
              ) : (
                <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Sincronización de Ingeniería Requerida
                </div>
              )}

              <p className="text-[10px] font-black text-slate-700 uppercase tracking-[0.3em]">Vertical Estándar de Alto Impacto / Fluxo Co</p>
            </motion.div>

          </div>
        ))}


      {/* Terminal View */}
      <AnimatePresence>
        {selectedSilo && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="pt-4"
          >
            {(() => {
              const activeSchema = SILO_FIELD_SCHEMAS[selectedSilo.name]; return (
                <Card className={`${glassBg} shadow-2xl overflow-hidden rounded-3xl border-white/10`}>
                  <CardHeader className="bg-white/5 border-b border-white/5 py-2.5 px-8 flex flex-row items-center justify-between">
                    <CardTitle className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-500 flex items-center gap-3">
                      <Activity className="w-4 h-4 text-emerald-400 animate-pulse" />
                      Silo: <span className="text-white font-black">{selectedSilo.name}</span>
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <button
                        className="text-[9px] font-black text-slate-500 hover:text-white transition-all border border-white/10 px-4 py-1.5 rounded-xl bg-white/5 hover:bg-red-500/10 hover:border-red-500/20 uppercase tracking-widest"
                        onClick={() => setSelectedSilo(null)}
                      >
                        Cerrar Visor
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 bg-slate-950/20">
                    <div id="silo-table-container" className="overflow-x-auto max-h-[600px] w-full custom-scrollbar relative">
                      <table className="w-full min-w-[1400px] text-left border-collapse">
                        <thead className="sticky top-0 z-20">
                          {activeSchema && selectedSilo.name === 'catalogos' ? (
                            <tr className="bg-white/5 border-b border-white/5">
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">SKU</th>
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Nombre</th>
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500 w-[20%]">Descripción</th>
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Categoría</th>
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Precio</th>
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Stock</th>
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Estado</th>
                              {customFields.map(f => (
                                <th key={f.name} className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-400 italic">
                                  {f.label}
                                </th>
                              ))}
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <span>Acciones</span>
                                  <button
                                    onClick={() => setIsAddFieldModalOpen(true)}
                                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-blue-400 border border-white/5"
                                    title="Añadir Campo"
                                  >
                                    <Settings2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </th>
                            </tr>
                          ) : activeSchema && selectedSilo.name === 'clientes' ? (
                            <tr className="bg-white/5 border-b border-white/5">
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Cliente / RFC</th>
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Contacto</th>
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Tipo</th>
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500">Valor (LTV)</th>
                              {customFields.map(f => (
                                <th key={f.name} className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-400 italic">
                                  {f.label}
                                </th>
                              ))}
                              <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <span>Acciones</span>
                                  <button
                                    onClick={() => setIsAddFieldModalOpen(true)}
                                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-blue-400 border border-white/5"
                                    title="Añadir Campo"
                                  >
                                    <Settings2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </th>
                            </tr>
                          ) : (
                            <tr className="text-[10px] font-black text-slate-500 uppercase tracking-widest bg-white/5">
                              <th className="px-6 py-5">Información del Registro</th>
                              <th className="px-6 py-5">Sincronización</th>
                              <th className="px-6 py-5 text-center">Acciones</th>
                            </tr>
                          )}
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {siloData
                            .filter(record =>
                              !siloSearchTerm ||
                              Object.values(record).some(val =>
                                String(val).toLowerCase().includes(siloSearchTerm.toLowerCase())
                              )
                            )
                            .map((record, index) => {
                              const { _id, engine_injected_at, engine_updated_at, fecha_registro, ...displayData } = record;

                              if (activeSchema && selectedSilo.name === 'catalogos') {
                                return (
                                  <motion.tr key={_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }} className="hover:bg-white/[0.02] transition-colors group">
                                    <td className="p-6">
                                      <code className="text-blue-400 font-mono text-[10px] bg-blue-500/10 px-2 py-1 rounded-lg border border-blue-500/20">
                                        {record.sku || (_id ? _id.substring(0, 8) : 'N/A')}
                                      </code>
                                    </td>
                                    <td className="p-6">
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-600/20 flex items-center justify-center border border-white/5 shrink-0 text-amber-500">
                                          {(parseFloat(record.precio) || 0) >= 1000 ? <Star className="w-3.5 h-3.5" /> : <Box className="w-3.5 h-3.5" />}
                                        </div>
                                        <span className="text-white font-bold text-sm tracking-wide">{record.nombre || 'Sin nombre'}</span>
                                      </div>
                                    </td>
                                    <td className="p-6">
                                      <p className="text-slate-400 text-xs line-clamp-2 max-w-[200px]">{record.descripcion || 'Sin descripción'}</p>
                                    </td>
                                    <td className="p-6">
                                      <div className="flex items-center gap-2">
                                        <Layers className="w-4 h-4 text-slate-500" />
                                        <span className="text-slate-300 font-medium text-sm">{record.categoria || 'Sin Clasificar'}</span>
                                      </div>
                                    </td>
                                    <td className="p-6">
                                      <span className="text-amber-400 font-black text-sm">${parseFloat(record.precio || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                      <span className="text-[9px] text-slate-600 ml-1">MXN</span>
                                    </td>
                                    <td className="p-6">
                                      <span className="text-slate-300 font-bold text-sm">{record.stock || record.cantidad || 0}</span>
                                    </td>
                                    <td className="p-6">
                                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(record.estado)}`}>
                                        {record.estado || 'ACTIVO'}
                                      </span>
                                    </td>
                                    {customFields.map(f => (
                                      <td key={f.name} className="p-6">
                                        <span className="text-slate-400 text-xs font-bold italic">
                                          {record[f.name] || '—'}
                                        </span>
                                      </td>
                                    ))}
                                    <td className="p-6 text-right whitespace-nowrap">
                                      <button onClick={() => handleEditClick(record)} className="p-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500 hover:text-white rounded-xl transition-all opacity-0 group-hover:opacity-100 shadow-sm mr-2" title="Modificar">
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => handleDeleteRecord(_id)} className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white rounded-xl transition-all opacity-0 group-hover:opacity-100 shadow-sm" title="Eliminar">
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </td>
                                  </motion.tr>
                                );
                              }

                              if (activeSchema && selectedSilo.name === 'clientes') {
                                return (
                                  <motion.tr key={_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }} className="hover:bg-white/[0.02] transition-colors group">
                                    <td className="p-6">
                                      <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 flex items-center justify-center text-white font-black text-xs shadow-lg border border-white/10 group-hover:scale-110 transition-transform">
                                          {record.nombre ? (record.nombre[0] + (record.nombre.split(' ').length > 1 ? record.nombre.split(' ').pop()[0] : '')).toUpperCase() : 'X'}
                                        </div>
                                        <div>
                                          <p className="text-white font-bold text-sm leading-tight">{record.nombre || 'Sin Nombre'}</p>
                                          <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{record.rfc || 'SIN RFC'}</span>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="p-6 text-slate-400 text-xs font-medium">
                                      {record.telefono} <br /> <span className="text-slate-600 italic">{record.email}</span>
                                    </td>
                                    <td className="p-6">
                                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border border-blue-500/30 text-blue-400 bg-blue-500/10`}>
                                        {record.tipo || 'LEAD'}
                                      </span>
                                    </td>
                                    <td className="p-6 text-emerald-400 font-black text-sm">
                                      ${parseFloat(record.ltv || 0).toLocaleString()} <span className="text-[9px] text-slate-600">MXN</span>
                                    </td>
                                    {customFields.map(f => (
                                      <td key={f.name} className="p-6 text-slate-500 text-xs italic">
                                        {record[f.name] || '—'}
                                      </td>
                                    ))}
                                    <td className="p-6 text-right whitespace-nowrap">
                                      <button onClick={() => handleEditClick(record)} className="p-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500 hover:text-white rounded-xl transition-all opacity-0 group-hover:opacity-100 shadow-sm mr-2">
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => handleDeleteRecord(_id)} className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white rounded-xl transition-all opacity-0 group-hover:opacity-100 shadow-sm">
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </td>
                                  </motion.tr>
                                );
                              }

                              return (
                                <tr key={_id} className={`group hover:bg-white/5 transition-all duration-300 ${editingRecordId === _id ? 'bg-blue-500/10 border-l-2 border-blue-500' : ''}`}>
                                  <td className="px-6 py-5">
                                    <div className="flex flex-wrap gap-2">
                                      {Object.entries(displayData).map(([k, v]) => (
                                        <div key={k} className="px-3 py-1.5 rounded-lg bg-blue-500/5 border border-blue-500/10 text-[11px] font-medium transition-all group-hover:border-blue-500/30">
                                          <span className="text-blue-400 font-bold uppercase text-[9px] mr-2 opacity-60">{k}:</span>
                                          <span className="text-slate-200">{String(v)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="px-6 py-5">
                                    <div className="flex flex-col gap-1">
                                      <div className="flex items-center gap-2">
                                        <Clock className="w-3.5 h-3.5 text-slate-600" />
                                        <span className="text-[11px] font-bold text-slate-400">
                                          {engine_injected_at ? new Date(engine_injected_at).toLocaleTimeString() : 'Histórico'}
                                        </span>
                                      </div>
                                      {engine_updated_at && (
                                        <span className="text-[8px] font-black text-blue-500/50 uppercase tracking-tighter">Editado recientemente</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-5">
                                    <div className="flex items-center justify-center gap-2">
                                      <button onClick={() => handleEditClick(record)} className="p-2.5 rounded-xl bg-white/5 border border-white/5 text-slate-500 hover:text-blue-400 hover:border-blue-500/30 transition-all hover:scale-110" title="Editar">
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => handleDeleteRecord(_id)} className="p-2.5 rounded-xl bg-white/5 border border-white/5 text-slate-500 hover:text-red-400 hover:border-red-500/30 transition-all hover:scale-110" title="Eliminar">
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          {siloData.filter(record => !siloSearchTerm || Object.values(record).some(val => String(val).toLowerCase().includes(siloSearchTerm.toLowerCase()))).length === 0 && (
                            <tr>
                              <td colSpan="100%" className="p-20 text-center">
                                <div className="flex flex-col items-center justify-center max-w-md mx-auto">
                                  <div className="w-20 h-20 rounded-[2rem] bg-white/5 border border-white/10 flex items-center justify-center mb-6 shadow-2xl">
                                    <Package className="w-10 h-10 text-slate-700 animate-pulse" />
                                  </div>
                                  <p className="text-xl font-black text-white uppercase tracking-tighter italic">{siloSearchTerm ? 'Búsqueda Estéril' : 'Almacén Vacío'}</p>
                                  <p className="text-sm text-slate-500 mt-2 font-medium">
                                    {siloSearchTerm
                                      ? 'No hay coincidencias en este silo. Intenta con un término más general o verifica la sintaxis.'
                                      : 'Este silo de datos aún no tiene registros inyectados. Utiliza el botón de "Nueva Entrada" para comenzar.'}
                                  </p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
            {/* Remove inline form, moved to Sidebar */}
          </motion.div>
        )}
      </AnimatePresence>

      {/* SIDEBAR DESLIZABLE (Formulario de Inyección) */}
      <AnimatePresence>
        {isSidebarOpen && selectedSilo && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSidebarOpen(false)}
              className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[90]"
            />
            <motion.div
              initial={{ x: '100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 right-0 w-full max-w-md bg-slate-900 border-l border-white/10 shadow-2xl z-[100] flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10 bg-slate-900/50">
                <div className="flex flex-col">
                  <h3 className="text-lg font-black text-white uppercase tracking-widest flex items-center gap-2">
                    {isEditing ? <Settings className="w-5 h-5 text-amber-400 animate-spin-slow" /> : <Plus className="w-5 h-5 text-blue-400" />}
                    {isEditing ? 'Editar Registro' : (SILO_FIELD_SCHEMAS[selectedSilo.name] ? SILO_FIELD_SCHEMAS[selectedSilo.name].newAction : 'Nueva Entrada')}
                  </h3>
                  <p className="text-[10px] text-slate-500 uppercase font-bold tracking-[0.2em] mt-1">
                    Silo: {selectedSilo.display_name || selectedSilo.name}
                  </p>
                </div>
                <button onClick={() => setIsSidebarOpen(false)} className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white hover:bg-red-500/20 transition-all">
                  <Trash2 className="w-4 h-4 opacity-0 hidden" />
                  <span className="text-xs font-black">X</span>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-slate-950/30">
                {SILO_FIELD_SCHEMAS[selectedSilo.name] ? (
                  // FORMULARIO ESTRUCTURADO (SCHEMA)
                  <div className="space-y-5">
                    {renderFormFields(SILO_FIELD_SCHEMAS[selectedSilo.name].fields)}
                  </div>
                ) : (
                  // FORMULARIO GENÉRICO (KEY/VALUE)
                  <div className="space-y-5">
                    {formFields.map((field, idx) => (
                      <div key={idx} className="flex gap-3 items-end group animate-in slide-in-from-left-4 duration-300 bg-white/5 p-3 rounded-2xl border border-white/5">
                        <div className="flex-1 space-y-1">
                          <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1">Campo</label>
                          <input
                            value={field.key}
                            onChange={(e) => updateField(idx, 'key', e.target.value)}
                            className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-xs text-blue-300 focus:border-blue-500/50 outline-none transition-all font-bold uppercase"
                            placeholder="KEY"
                          />
                        </div>
                        <div className="flex-[2] space-y-1">
                          <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1">Valor</label>
                          <input
                            value={field.value}
                            onChange={(e) => updateField(idx, 'value', e.target.value)}
                            className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-xs text-white focus:border-blue-500/50 outline-none transition-all font-medium"
                            placeholder="Value"
                          />
                        </div>
                        <button onClick={() => handleRemoveField(idx)} className="p-2.5 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all mb-0.5">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    <button
                      onClick={handleAddField}
                      className="flex items-center justify-center gap-2 w-full py-4 border border-dashed border-white/10 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-white hover:border-blue-500/50 hover:bg-blue-500/5 transition-all"
                    >
                      <Plus className="w-4 h-4" /> Añadir Campo Personalizado
                    </button>

                    {!isEditing && (
                      <div className="pt-4 mt-6 border-t border-white/5 space-y-3">
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Atajos de Estructura</p>
                        <div className="grid grid-cols-2 gap-2">
                          <button onClick={() => applyPreset({ "nombre": "", "sku": "", "costo": "", "precio": "" })} className="p-2 rounded-xl bg-white/5 text-[9px] font-black text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/5 text-center">PRODUCTO</button>
                          <button onClick={() => applyPreset({ "servicio": "", "tarifa": "", "status": "activo" })} className="p-2 rounded-xl bg-white/5 text-[9px] font-black text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/5 text-center">SERVICIO</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-white/10 bg-slate-900">
                <button
                  onClick={SILO_FIELD_SCHEMAS[selectedSilo.name] ? handleSchemaSave : handleInject}
                  disabled={isInjecting}
                  className={`w-full py-4 rounded-2xl bg-gradient-to-r ${isEditing ? 'from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 shadow-amber-500/20' : 'from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-blue-500/20'} text-white font-black text-[11px] uppercase tracking-[0.2em] transition-all shadow-xl flex items-center justify-center gap-2 ${isInjecting ? 'opacity-50' : 'hover:-translate-y-1'}`}
                >
                  {isInjecting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                  {isInjecting ? (isEditing ? 'Actualizando...' : 'Guardando...') : (isEditing ? 'Actualizar Registro' : 'Guardar en Silo')}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* MODAL DE CONFIGURACIÓN DE SILO */}
      <AnimatePresence>
        {showConfigModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowConfigModal(false)}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={`relative w-full max-w-lg overflow-hidden rounded-[3rem] border border-white/10 shadow-2xl ${glassBg}`}
            >
              {/* Header */}
              <div className="bg-blue-600/20 p-8 border-b border-white/10 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-black text-white uppercase tracking-tighter">Configuración</h3>
                  <p className="text-[10px] font-bold text-blue-400/60 uppercase tracking-widest mt-1">Silo: {configuringSilo?.name}</p>
                </div>
                <div className="p-3 rounded-2xl bg-white/5 border border-white/10">
                  <Settings className="w-6 h-6 text-blue-400" />
                </div>
              </div>

              {/* Body */}
              <div className="p-8 space-y-6">
                {/* Nombre Amigable */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Nombre del Silo</label>
                  <input
                    type="text"
                    value={configData.display_name}
                    onChange={(e) => setConfigData({ ...configData, display_name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-sm text-white focus:border-blue-500/50 outline-none transition-all font-bold"
                    placeholder="Ej. Mi Inventario VIP"
                  />
                </div>

                {/* Switch Activo */}
                <div className="flex items-center justify-between p-5 rounded-2xl bg-white/5 border border-white/5">
                  <div>
                    <p className="text-xs font-black text-white uppercase tracking-widest">Silo Activo</p>
                    <p className="text-[10px] text-slate-500 font-bold uppercase mt-1">Permitir lectura de la IA</p>
                  </div>
                  <button
                    onClick={() => setConfigData({ ...configData, active: !configData.active })}
                    className={`w-12 h-6 rounded-full p-1 transition-all duration-300 ${configData.active ? 'bg-blue-600' : 'bg-slate-800'}`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-all duration-300 ${configData.active ? 'translate-x-6' : 'translate-x-0'}`} />
                  </button>
                </div>

                {/* Contexto IA */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Contexto Especial para la IA</label>
                  <textarea
                    value={configData.ai_context}
                    onChange={(e) => setConfigData({ ...configData, ai_context: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-sm text-slate-300 focus:border-blue-500/50 outline-none transition-all font-medium h-32 resize-none"
                    placeholder="Explícale a la IA cómo usar estos datos..."
                  />
                </div>

                {/* Zona de Peligro: Solo para silos Custom (Fase 10.6) y SOLO en Consola Maestra */}
                {!contextTenantSlug && configuringSilo?.category === 'custom' && (
                  <div className="mt-8 p-6 rounded-2xl bg-red-500/5 border border-red-500/20 space-y-4">
                    <div className="flex items-center gap-2 text-red-500">
                      <Trash2 className="w-4 h-4" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Zona de Peligro (Borrado Físico)</span>
                    </div>

                    <p className="text-[10px] text-slate-400 leading-relaxed font-medium">
                      Esta acción destruirá permanentemente la colección <span className="text-red-400 font-bold">"{configuringSilo.name}"</span> en MongoDB y todos sus registros. No se puede deshacer.
                    </p>

                    <div className="space-y-2">
                      <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Escribe el nombre del silo para confirmar:</label>
                      <input
                        type="text"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        placeholder={configuringSilo.name}
                        className="w-full bg-slate-950/40 border border-white/5 rounded-xl px-4 py-3 text-[11px] text-red-400 font-mono focus:border-red-500/50 outline-none transition-all"
                      />
                    </div>

                    <button
                      onClick={deleteSilo}
                      disabled={isDeleting || deleteConfirmation !== configuringSilo.name}
                      className={`w-full py-3 rounded-xl font-black text-[10px] uppercase tracking-widest border transition-all ${deleteConfirmation === configuringSilo.name
                          ? 'bg-red-600 text-white border-red-500 hover:bg-red-500 shadow-lg shadow-red-900/20'
                          : 'bg-white/5 text-slate-600 border-white/5 cursor-not-allowed'
                        }`}
                    >
                      {isDeleting ? 'Eliminando...' : 'Eliminar Silo para Siempre'}
                    </button>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-8 pt-0 flex gap-4">
                <button
                  onClick={() => setShowConfigModal(false)}
                  className="flex-1 py-4 rounded-2xl bg-white/5 border border-white/10 text-[10px] font-black uppercase text-slate-400 hover:text-white transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveSiloConfig}
                  className="flex-[2] py-4 rounded-2xl bg-blue-600 hover:bg-blue-500 text-[10px] font-black uppercase text-white transition-all shadow-xl shadow-blue-900/40"
                >
                  Guardar Cambios
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Bulk Upload Modal */}
      <Dialog open={isBulkModalOpen} onOpenChange={setIsBulkModalOpen}>
        <DialogContent className="max-w-2xl bg-slate-900/95 border-white/10 backdrop-blur-xl text-white rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-black uppercase tracking-widest flex items-center gap-3">
              <UploadCloud className="w-6 h-6 text-blue-400" />
              Carga Masiva de Datos
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 space-y-6">
            <div className="flex gap-4">
              <button
                onClick={() => setBulkFormat("json")}
                className={`flex-1 py-3 rounded-2xl border ${bulkFormat === 'json' ? 'bg-blue-600 border-blue-400 shadow-[0_0_20px_rgba(37,99,235,0.4)]' : 'bg-white/5 border-white/10'} font-bold transition-all text-xs uppercase tracking-widest`}
              >
                JSON Array
              </button>
              <button
                onClick={() => setBulkFormat("csv")}
                className={`flex-1 py-3 rounded-2xl border ${bulkFormat === 'csv' ? 'bg-blue-600 border-blue-400 shadow-[0_0_20px_rgba(37,99,235,0.4)]' : 'bg-white/5 border-white/10'} font-bold transition-all text-xs uppercase tracking-widest`}
              >
                CSV / List
              </button>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                Pega tus datos aquí ({bulkFormat.toUpperCase()})
              </label>
              <textarea
                className="w-full h-64 bg-black/40 border border-white/10 rounded-2xl p-6 font-mono text-xs text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all resize-none"
                placeholder={bulkFormat === 'json' ? '[{"sku": "A1", "nombre": "Prod 1"}, ...]' : 'sku, nombre, precio\nA1, Prod 1, 100'}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button onClick={() => setIsBulkModalOpen(false)} className="px-6 py-3 rounded-2xl bg-white/5 text-slate-400 font-bold uppercase tracking-widest text-xs hover:bg-white/10 transition-all">
                Cancelar
              </button>
              <button
                onClick={handleBulkUpload}
                disabled={!bulkText}
                className="px-8 py-3 rounded-2xl bg-blue-600 text-white font-bold uppercase tracking-widest text-xs hover:bg-blue-500 transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] disabled:opacity-50"
              >
                Procesar Inyección
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Field Modal */}
      <Dialog open={isAddFieldModalOpen} onOpenChange={setIsAddFieldModalOpen}>
        <DialogContent className="max-w-md bg-slate-900/95 border-white/10 backdrop-blur-xl text-white rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-black uppercase tracking-widest flex items-center gap-3">
              <Settings2 className="w-6 h-6 text-amber-400" />
              Añadir Campo al Silo
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Nombre del Nuevo Campo</label>
              <input
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 transition-all"
                placeholder="Ej: Proveedor, Ubicación, etc."
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Tipo de Dato</label>
              <div className="grid grid-cols-3 gap-2">
                {['text', 'number', 'currency'].map(type => (
                  <button
                    key={type}
                    onClick={() => setNewFieldType(type)}
                    className={`py-2 rounded-xl border text-[10px] font-bold uppercase transition-all ${newFieldType === type ? 'bg-amber-600 border-amber-400' : 'bg-white/5 border-white/10'}`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button onClick={() => setIsAddFieldModalOpen(false)} className="px-6 py-3 rounded-2xl bg-white/5 text-slate-400 font-bold uppercase tracking-widest text-xs hover:bg-white/10 transition-all">
                Cancelar
              </button>
              <button
                onClick={handleAddCustomField}
                disabled={!newFieldName}
                className="px-8 py-3 rounded-2xl bg-amber-600 text-white font-bold uppercase tracking-widest text-xs hover:bg-amber-500 transition-all shadow-[0_0_20px_rgba(245,158,11,0.3)] disabled:opacity-50"
              >
                Añadir Campo
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LoaderComponent = ({ color }) => (
  <div className="relative">
    <RefreshCw className="w-16 h-16 animate-spin-slow opacity-20" style={{ color }} />
    <div className="absolute inset-0 flex items-center justify-center">
      <Sparkles className="w-8 h-8 animate-pulse text-blue-400" />
    </div>
  </div>
);

export default DataEnginePage;
